import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { getHeaders } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Props {
  propertyId?: string | undefined;
  currentIntegration?: any | null;
}

const PayrollCard: React.FC<Props> = ({ propertyId, currentIntegration }) => {
  const { toast } = useToast();
  const [frequency, setFrequency] = useState<string>("Weekly");
  const [processingDay, setProcessingDay] = useState<string>("Friday");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      // const integrationId = currentIntegration?.integrationId || currentIntegration?.id || null;
      const headers = await getHeaders();
      // Persist each setting separately by calling /settings for each key
      const settingsToSave: Array<{ key: string; value: string }> = [
        { key: "payroll_week", value: frequency },
        { key: "payroll_day", value: processingDay },
      ];

      for (const s of settingsToSave) {
        const payload = { propertyId: propertyId, uid: s.key, value: s.value } ;
        const resp = await fetch(`${import.meta.env.VITE_API_URL}/settings`, {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.message || `Failed to save setting ${s.key}: ${resp.status}`);
        }
      }

      toast({ title: "Saved", description: "Payroll settings saved." });
    } catch (err: any) {
      console.error("Error saving payroll settings", err);
      toast({ title: "Error", description: err.message || "Failed to save payroll settings.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const normalizeFrequency = (val: string) => {
    if (!val) return "Weekly";
    const v = val.toString().toLowerCase();
    if (v.includes("fort") || v.includes("2") || v.includes("bi")) return "Fortnightly";
    return "Weekly";
  };

  const normalizeDay = (val: string) => {
    if (!val) return "Friday";
    const d = val.toString().toLowerCase();
    const days = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
    const found = days.find((day) => d.includes(day));
    if (found) return found[0].toUpperCase() + found.slice(1);
    // fallback: capitalize
    return val.charAt(0).toUpperCase() + val.slice(1);
  };

  const fetchSettings = async () => {
    if (!propertyId) return;
    try {
      const headers = await getHeaders();
      const url = `${import.meta.env.VITE_API_URL}/settings?propertyId=${propertyId}&keys=payroll_week,payroll_day`;
      const resp = await fetch(url, { method: "GET", headers });
      if (!resp.ok) throw new Error(`Failed to fetch settings: ${resp.status}`);
      const data = await resp.json();

      // Support multiple shapes: object map or array of {key, value}
      let weekVal: string | undefined;
      let dayVal: string | undefined;

      if (Array.isArray(data)) {
        for (const item of data) {
          if (!item) continue;
          const k = item.key || item.uid || item.uid;
          const v = item.value ?? item.val ?? item.value;
          if (k === "payroll_week") weekVal = v;
          if (k === "payroll_day") dayVal = v;
        }
      } else if (data && typeof data === "object") {
        // direct map or { settings: { ... } }
        const map = data.settings || data;
        if (map.payroll_week) weekVal = map.payroll_week;
        if (map.payroll_day) dayVal = map.payroll_day;
        if (map["payroll_week"]) weekVal = map["payroll_week"];
        if (map["payroll_day"]) dayVal = map["payroll_day"];
      }

      if (weekVal) setFrequency(normalizeFrequency(weekVal));
      if (dayVal) setProcessingDay(normalizeDay(dayVal));
    } catch (err) {
      console.error("Error loading payroll settings", err);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payroll</CardTitle>
        <CardDescription>Configure payroll frequency and processing day for this property.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="frequency">Payment Frequency</Label>
            <select
              id="frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="mt-1 block w-full rounded-md border px-2 py-1 text-sm"
            >
              <option value="Weekly">Weekly</option>
              <option value="Fortnightly">Fortnightly</option>
            </select>
          </div>

          <div>
            <Label htmlFor="processingDay">Payroll Processing Day</Label>
            <select
              id="processingDay"
              value={processingDay}
              onChange={(e) => setProcessingDay(e.target.value)}
              className="mt-1 block w-full rounded-md border px-2 py-1 text-sm"
            >
              <option>Monday</option>
              <option>Tuesday</option>
              <option>Wednesday</option>
              <option>Thursday</option>
              <option>Friday</option>
              <option>Saturday</option>
              <option>Sunday</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <Button onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Payroll Settings"}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PayrollCard;
